import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  Container, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Button, Typography, Box, Chip, 
  Tooltip, IconButton, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, CircularProgress 
} from '@mui/material';
import axios from '../../api/axiosConfig'; 
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const QuestionList = () => {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // 조회 관련 상태 추가
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  const navigate = useNavigate();
  const THEME_NAVY = '#1a233a';

  // 카테고리 목록은 화면 진입 시 미리 가져옴 (검색 조건용)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const cRes = await axios.get('/api/category');
        setCategories(cRes.data);
      } catch (err) {
        console.error("카테고리 로딩 실패:", err);
      }
    };
    fetchCategories();
  }, []);

  // 실제 문제 목록 조회 함수
  const fetchData = useCallback(async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const qRes = await axios.get('/api/question');
      setQuestions(qRes.data);
    } catch (err) {
      console.error("데이터 로딩 실패:", err);
      if (err.response?.status === 403) {
        alert("접근 권한이 없습니다. 다시 로그인해 주세요.");
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // [수정] useEffect에서 fetchData() 자동 호출 제거

  const filteredQuestions = useMemo(() => {
    return questions.filter(q => {
      const matchCategory = selectedCategory === 'all' || q.categoryPk === selectedCategory;
      const matchSearch = 
        (q.title?.toLowerCase().includes(searchTerm.toLowerCase())) || 
        (q.question?.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchCategory && matchSearch;
    });
  }, [selectedCategory, searchTerm, questions]);

  const handleDelete = async (pk) => {
    if (!window.confirm('정말 이 문제를 삭제하시겠습니까?')) return;
    try {
      await axios.delete(`/api/question/${pk}`);
      alert('삭제되었습니다.');
      fetchData();
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleReset = () => {
    setSelectedCategory('all');
    setSearchTerm('');
    setQuestions([]);
    setHasSearched(false);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: THEME_NAVY }}>
            문제 마스터 관리
          </Typography>
          {hasSearched && (
            <Chip 
              label={`결과 ${filteredQuestions.length}건 / 전체 ${questions.length}건`} 
              sx={{ ml: 2, fontWeight: 'bold' }} 
              size="small" 
              color="primary" 
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="contained" 
            onClick={() => navigate('/create')}
            sx={{ backgroundColor: THEME_NAVY, "&:hover": { backgroundColor: "#2c3e50" }, fontWeight: 'bold' }}
          >
            새 문제 등록
          </Button>
        </Box>
      </Box>

      {/* 검색 바 영역 */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, backgroundColor: '#fdfdfd' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon sx={{ color: THEME_NAVY }} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>카테고리</InputLabel>
            <Select
              value={selectedCategory}
              label="카테고리"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="all">전체 카테고리</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.categoryPk} value={cat.categoryPk}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          size="small"
          placeholder="문제 제목 또는 내용 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchData()}
          sx={{ flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* [수정] 조회 버튼 추가 및 초기화 버튼 위치 변경 */}
        <Button 
          variant="contained" 
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />} 
          onClick={fetchData}
          sx={{ bgcolor: THEME_NAVY, fontWeight: 'bold' }}
        >
          조회
        </Button>

        <Button 
          startIcon={<RefreshIcon />} 
          size="small" 
          color="inherit"
          onClick={handleReset}
        >
          초기화
        </Button>
      </Paper>

      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, position: 'relative', maxHeight: 'calc(100vh - 415px)' }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '60px' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>카테고리</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>문제 제목</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '80px' }}>배점</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>등록 정보</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>수정 정보</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', width: '120px' }}>관리</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            ) : !hasSearched ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 15 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1 }} />
                  <Typography color="text.secondary" fontWeight="bold">
                    조회 버튼을 클릭하여 문제 목록을 확인하세요.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredQuestions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8, color: 'gray' }}>
                  조건에 맞는 문제가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              filteredQuestions.map((q) => (
                <TableRow key={q.questionPk} hover>
                  <TableCell align="center" sx={{ color: '#888' }}>{q.questionPk}</TableCell>
                  <TableCell>
                    <Chip 
                      label={q.categoryName || "미분류"} 
                      size="small" 
                      variant="filled"
                      sx={{ backgroundColor: '#e8eaf6', color: THEME_NAVY, fontWeight: 'bold' }} 
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="상세보기 클릭">
                      <Typography sx={{ 
                        fontWeight: '500', 
                        maxWidth: 400, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                      }} onClick={() => navigate(`/questions/${q.questionPk}`)}>
                        {q.title}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  
                  <TableCell align="center">
                    <Typography sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      {q.score !== undefined && q.score !== null ? `${q.score}점` : '0점'}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                      {q.createUser || 'SYSTEM'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(q.createDate)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                      {q.updateUser || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {q.updateDate ? formatDateTime(q.updateDate) : '-'}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => navigate(`/questions/${q.questionPk}`)}>
                        <SearchIcon fontSize="small" color="action" />
                      </IconButton>
                      <IconButton size="small" onClick={() => navigate(`/edit/${q.questionPk}`)}>
                        <EditIcon fontSize="small" color="primary" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(q.questionPk)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default QuestionList;